import React, { useState } from 'react'
import { CheckCircle, Clock, X, Loader2, AlertCircle } from 'lucide-react'
import api from '../../services/api'
import toast from 'react-hot-toast'

/**
 * Authoritative Admission Fee Detection Helper
 * Primary rule: payment_purpose === 'ADMISSION' or backend is_admission_fee flag.
 * Controlled legacy fallback: description 'Admission Fees' with course_id IS NULL and masterclass_id IS NULL.
 */
export function isAdmissionFeeInvoice(invoice) {
  if (!invoice) return false
  if (invoice.payment_purpose === 'ADMISSION' || invoice.is_admission_fee === true) {
    return true
  }
  const desc = String(invoice.description || '').trim().toLowerCase()
  const hasNoCourse = !invoice.course_id && !invoice.masterclass_id
  return hasNoCourse && (desc === 'admission fees' || desc === 'admission fee')
}

/**
 * Cardless Quick Confirmation Dialog for Fixed-Amount Admission Fees
 * Follows SMA Cardless Design Language:
 * - Flat surfaces without nested cards
 * - Semantic information blocks, labels, and clean dividers
 * - Non-editable fixed amount (installment amounts forbidden)
 * - Server-side reference and notes defaults
 * - Strict authoritative posting (no optimistic status flipping)
 * - Concurrency protection handling
 *
 * Theme-aware: white surface in light mode, dark elevated surface in dark mode.
 * SMA brand: orange primary action, green amount, orange unpaid state.
 */
export default function ConfirmAdmissionPaymentModal({
  isOpen,
  onClose,
  invoice,
  onSuccess,
}) {
  const [paymentMethod, setPaymentMethod] = useState('cash')
  const [paymentDate, setPaymentDate] = useState(() => new Date().toISOString().split('T')[0])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)

  if (!isOpen || !invoice) return null

  const studentName = invoice.student_name ||
    (invoice.first_name ? `${invoice.first_name} ${invoice.last_name || ''}`.trim() : 'Prospective Student')
  const studentEmail = invoice.student_email || invoice.email || ''
  const invoiceNumber = invoice.invoice_number || invoice.admission_invoice_number || `INV-${invoice.id}`
  const feeAmount = Number(invoice.amount || invoice.admission_fee || 50000)

  const handleConfirm = async () => {
    setIsSubmitting(true)
    setErrorMessage(null)

    try {
      // 1. Post to authoritative payment endpoint
      const invoiceId = invoice.id || invoice.admission_invoice_id
      const payload = {
        invoice_id: invoiceId,
        amount: feeAmount,
        payment_method: paymentMethod,
        payment_date: paymentDate,
        // Transaction reference and notes will use server-side generation fallback if omitted
      }

      const response = await api.post('/finance/payments', payload)
      const data = response.data

      toast.success(data.message || 'Admission fee payment recorded successfully')

      // 2. Authoritative update on frontend strictly after successful backend response
      if (typeof onSuccess === 'function') {
        onSuccess({
          invoiceId,
          payment: data.payment,
          receiptNumber: data.receipt_number,
          invoiceStatus: data.invoice_status || 'paid',
          newAmountPaid: data.new_amount_paid || feeAmount,
          remainingBalance: data.remaining_balance ?? 0,
        })
      }

      onClose()
    } catch (err) {
      const serverError = err.response?.data?.error || 'Failed to record admission fee payment'
      setErrorMessage(serverError)
      toast.error(serverError)

      // If already paid (concurrency scenario), inform parent to refresh data
      if (serverError.toLowerCase().includes('already') || serverError.toLowerCase().includes('paid')) {
        if (typeof onSuccess === 'function') {
          onSuccess({ refreshOnly: true })
        }
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 dark:bg-black/80 backdrop-blur-sm p-4 animate-in fade-in duration-150">
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 w-full max-w-md p-6 sm:p-8 shadow-2xl relative text-left">
        {/* Header */}
        <div className="flex items-start justify-between pb-3 border-b border-gray-200 dark:border-gray-800 mb-5">
          <div>
            <h2 className="font-display text-lg sm:text-xl text-gray-900 dark:text-white font-semibold tracking-wide uppercase">
              Confirm Admission Payment
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-900 dark:hover:text-white transition-colors p-1"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Error Banner */}
        {errorMessage && (
          <div className="mb-5 p-3.5 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 flex items-start gap-2.5 text-xs text-red-700 dark:text-red-300">
            <AlertCircle size={16} className="shrink-0 mt-0.5 text-red-500 dark:text-red-400" />
            <div>
              <p className="font-medium text-red-800 dark:text-red-200">Payment rejected</p>
              <p className="mt-0.5">{errorMessage}</p>
            </div>
          </div>
        )}

        {/* Content Block: Cardless Information Flow */}
        <div className="space-y-4">
          {/* Student */}
          <div>
            <p className="font-sans text-base sm:text-lg font-semibold text-gray-900 dark:text-white truncate">
              {studentName}
            </p>
            {studentEmail && (
              <p className="font-sans text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">{studentEmail}</p>
            )}
            {invoiceNumber && (
              <p className="font-mono text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">{invoiceNumber}</p>
            )}
          </div>

          {/* Admission Fee & Status */}
          <div className="grid grid-cols-2 gap-4 pt-2">
            <div>
              <span className="text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-400 font-sans block mb-1">
                Admission Fee
              </span>
              <p className="font-display text-xl font-semibold text-green-600 dark:text-green-400">
                UGX {feeAmount.toLocaleString()}
              </p>
            </div>
            <div>
              <span className="text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-400 font-sans block mb-1">
                Status
              </span>
              <div className="mt-1">
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 text-xs text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-500/10 border border-primary-200 dark:border-primary-500/20 font-medium">
                  <Clock size={12} />
                  <span>Unpaid</span>
                </span>
              </div>
            </div>
          </div>

          {/* Payment Method */}
          <div className="pt-2">
            <label className="block text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1.5 font-sans">
              Payment Method
            </label>
            <select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              disabled={isSubmitting}
              className="w-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white text-sm px-3 py-2 focus:border-primary-500 focus:outline-none transition-colors capitalize"
            >
              <option value="cash">Cash</option>
              <option value="mobile_money">Mobile Money</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="mtn_momo">MTN MoMo</option>
              <option value="airtel_money">Airtel Money</option>
              <option value="cheque">Cheque</option>
            </select>
          </div>

          {/* Payment Date */}
          <div className="pt-1">
            <label className="block text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1.5 font-sans">
              Payment Date
            </label>
            <input
              type="date"
              value={paymentDate}
              max={new Date().toISOString().split('T')[0]}
              onChange={(e) => setPaymentDate(e.target.value)}
              disabled={isSubmitting}
              className="w-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white text-sm px-3 py-2 focus:border-primary-500 focus:outline-none transition-colors font-sans"
            />
          </div>
        </div>

        {/* Hairline Divider and Actions */}
        <div className="flex items-center justify-end gap-3 pt-5 mt-6 border-t border-gray-200 dark:border-gray-800">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isSubmitting}
            className="flex items-center gap-2 px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white text-xs uppercase tracking-wider font-semibold shadow-lg shadow-primary-500/20 disabled:opacity-50 transition-all cursor-pointer"
          >
            {isSubmitting ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                <span>Processing…</span>
              </>
            ) : (
              <>
                <CheckCircle size={14} />
                <span>Confirm Payment</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
